import { useEffect, useState } from 'react';

declare const __BUILD_ID__: string;

/**
 * Noticing that a new version has been deployed.
 *
 * A single-page app keeps running the JavaScript it was loaded with. Deploy
 * twice in a school day and half the staff are still on the morning's bundle —
 * calling an endpoint that has changed shape, or missing the fix that was the
 * point of deploying. Nothing tells them, because nothing asks.
 *
 * So the tab asks. It knows the id it was built with, and `version.json` says
 * what the server is serving now. Different means stale.
 *
 * Two things make this cheap. It is a static file, so it survives an expired
 * session and a dead API — precisely the states in which finding out matters
 * most. And the check is driven by the tab becoming visible rather than by a
 * fast timer, because the moment a teacher comes back to a tab they left open
 * since Monday is exactly when it is most likely to be out of date.
 */

const POLL_INTERVAL_MS = 15 * 60 * 1000;

/** In development the bundle is swapped in place; there is nothing to nag about. */
const isDevelopment = __BUILD_ID__ === 'development';

async function servedBuildId(): Promise<string | null> {
  try {
    // `no-store` and not just a cache-busting query: a CDN told to cache
    // aggressively would otherwise keep answering with the old id, and this
    // file is the one thing that must never be served stale.
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return null;
    const payload: unknown = await response.json();
    const id = (payload as { buildId?: unknown } | null)?.buildId;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    // Offline, or mid-deploy. Not an error worth showing anybody.
    return null;
  }
}

export function useUpdateAvailable(): { available: boolean; dismiss: () => void } {
  const [servedId, setServedId] = useState<string | null>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  useEffect(() => {
    if (isDevelopment) return;

    let cancelled = false;

    const check = async () => {
      const id = await servedBuildId();
      if (!cancelled && id) setServedId(id);
    };

    void check();

    const timer = setInterval(() => void check(), POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const stale = servedId !== null && servedId !== __BUILD_ID__;

  return {
    // Dismissal is per build, not forever: saying "not now" to Tuesday's
    // release should not silence Thursday's.
    available: stale && servedId !== dismissedId,
    dismiss: () => setDismissedId(servedId),
  };
}
